import { GroupOrderState, PaymentMode, GroupOrderStatus } from "./types";
import * as ACTION_TYPE from "./constants";

const initialState: GroupOrderState = {
  active: false,
  isOrganizer: false,
  groupOrderId: null,
  joinCode: null,
  shareableLink: null,
  qrCodeBase64: null,
  participantId: null,
  participantName: null,
  paymentMode: PaymentMode.ORGANIZER_PAYS_ALL,
  groupOrder: null,
  localItems: [],
  lastModified: null,
  activityEvents: [],
  participantIdentities: {},
  creating: false,
  createError: null,
  joining: false,
  joinError: null,
  syncing: false,
  syncError: null,
  closing: false,
  closeError: null,
  cancelling: false,
  cancelError: null,
  polling: false,
  paymentInProgress: false,
  paymentError: null,
};

export default function groupOrderReducer(
  state = initialState,
  action: any,
): GroupOrderState {
  switch (action.type) {
    // ── Create ──
    case ACTION_TYPE.CREATE_GROUP_ORDER.STARTED:
      return { ...state, creating: true, createError: null };
    case ACTION_TYPE.CREATE_GROUP_ORDER.SUCCESS: {
      const { groupOrderId, joinCode, shareableLink, qrCodeBase64, participantId, paymentMode, branchId, servingOptionType, organizerName } = action.payload;
      return {
        ...state,
        active: true,
        isOrganizer: true,
        groupOrderId,
        joinCode,
        shareableLink,
        qrCodeBase64: qrCodeBase64 || null,
        participantId,
        participantName: organizerName,
        paymentMode,
        creating: false,
        createError: null,
      };
    }
    case ACTION_TYPE.CREATE_GROUP_ORDER.FAILED:
      return { ...state, creating: false, createError: action.payload };

    // ── Join ──
    case ACTION_TYPE.JOIN_GROUP_ORDER.STARTED:
      return { ...state, joining: true, joinError: null };
    case ACTION_TYPE.JOIN_GROUP_ORDER.SUCCESS: {
      const { groupOrderId, joinCode, participantId, participantName, paymentMode, groupOrder } = action.payload;
      return {
        ...state,
        active: true,
        isOrganizer: false,
        groupOrderId,
        joinCode,
        participantId,
        participantName,
        paymentMode: paymentMode ?? state.paymentMode,
        groupOrder: groupOrder || state.groupOrder,
        joining: false,
        joinError: null,
      };
    }
    case ACTION_TYPE.JOIN_GROUP_ORDER.FAILED:
      return { ...state, joining: false, joinError: action.payload };

    // ── Poll ──
    case ACTION_TYPE.POLL_GROUP_ORDER.STARTED:
      return { ...state, polling: true };
    case ACTION_TYPE.POLL_GROUP_ORDER.SUCCESS: {
      if (!action.payload) {
        // 304 — not modified
        return { ...state, polling: false };
      }
      return {
        ...state,
        groupOrder: action.payload,
        lastModified: action.payload.lastModified || state.lastModified,
        polling: false,
      };
    }
    case ACTION_TYPE.POLL_GROUP_ORDER.FAILED:
      return { ...state, polling: false };

    // ── Sync items ──
    case ACTION_TYPE.SYNC_PARTICIPANT_ITEMS.STARTED:
      return { ...state, syncing: true, syncError: null };
    case ACTION_TYPE.SYNC_PARTICIPANT_ITEMS.SUCCESS:
      return { ...state, syncing: false, syncError: null };
    case ACTION_TYPE.SYNC_PARTICIPANT_ITEMS.FAILED:
      return { ...state, syncing: false, syncError: action.payload };

    // ── Close ──
    case ACTION_TYPE.CLOSE_GROUP_ORDER.STARTED:
      return { ...state, closing: true, closeError: null };
    case ACTION_TYPE.CLOSE_GROUP_ORDER.SUCCESS:
      return { ...state, closing: false, closeError: null };
    case ACTION_TYPE.CLOSE_GROUP_ORDER.FAILED:
      return { ...state, closing: false, closeError: action.payload };

    // ── Cancel ──
    case ACTION_TYPE.CANCEL_GROUP_ORDER.STARTED:
      return { ...state, cancelling: true, cancelError: null };
    case ACTION_TYPE.CANCEL_GROUP_ORDER.SUCCESS:
      return { ...initialState };
    case ACTION_TYPE.CANCEL_GROUP_ORDER.FAILED:
      return { ...state, cancelling: false, cancelError: action.payload };

    // ── Payment (EPO) ──
    case ACTION_TYPE.BEGIN_PARTICIPANT_PAYMENT.STARTED:
      return { ...state, paymentInProgress: true, paymentError: null };
    case ACTION_TYPE.BEGIN_PARTICIPANT_PAYMENT.SUCCESS:
      return { ...state, paymentInProgress: true };
    case ACTION_TYPE.BEGIN_PARTICIPANT_PAYMENT.FAILED:
      return { ...state, paymentInProgress: false, paymentError: action.payload };

    case ACTION_TYPE.APPROVE_PARTICIPANT_PAYMENT.STARTED:
      return { ...state, paymentInProgress: true, paymentError: null };
    case ACTION_TYPE.APPROVE_PARTICIPANT_PAYMENT.SUCCESS:
      return { ...state, paymentInProgress: false, paymentError: null };
    case ACTION_TYPE.APPROVE_PARTICIPANT_PAYMENT.FAILED:
      return { ...state, paymentInProgress: false, paymentError: action.payload };

    // ── Local item management ──
    case ACTION_TYPE.ADD_LOCAL_ITEM:
      return { ...state, localItems: [...state.localItems, action.payload] };
    case ACTION_TYPE.REMOVE_LOCAL_ITEM:
      return {
        ...state,
        localItems: state.localItems.filter((_, i) => i !== action.payload),
      };
    case ACTION_TYPE.UPDATE_LOCAL_ITEM_QUANTITY: {
      const { index, quantity } = action.payload;
      const items = [...state.localItems];
      if (items[index]) {
        items[index] = { ...items[index], quantity };
      }
      return { ...state, localItems: items };
    }
    case ACTION_TYPE.CLEAR_LOCAL_ITEMS:
      return { ...state, localItems: [] };

    // ── Activity events ──
    case ACTION_TYPE.ACTIVITY_EVENT:
      return {
        ...state,
        activityEvents: [...state.activityEvents.slice(-19), action.payload],
      };
    case ACTION_TYPE.CLEAR_ACTIVITY_EVENTS:
      return { ...state, activityEvents: [] };

    // ── Participant identity ──
    case ACTION_TYPE.SET_PARTICIPANT_IDENTITY: {
      const { participantId, identity } = action.payload;
      return {
        ...state,
        participantIdentities: {
          ...state.participantIdentities,
          [participantId]: identity,
        },
      };
    }

    // ── Session management ──
    case ACTION_TYPE.SET_ACTIVE:
      return { ...state, active: action.payload };
    case ACTION_TYPE.LEAVE_GROUP_ORDER:
      return { ...initialState };

    default:
      return state;
  }
}
